import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fetchAuthSession } from '@aws-amplify/auth';
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Paper,
  Chip,
} from '@mui/material';
import { SECTION_LABELS, SECTIONS_ORDER } from '../services/admissionStreamService';
import { SectionRenderer } from '../components/SectionRenderer';
import config from '../utils/Constants';


export default function CyntheticAdmissionDetails() {
  const { uuid } = useParams();
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // First try Redux streaming state (available during the same session). The
  // streaming slice only ever holds the current tenant's charts.
  const streamState = useSelector((state) => state.streaming?.admissions?.[uuid]);
  const selectedTenant = useSelector((state) => state.user?.selectedTenant);
  const selectedTenantId = selectedTenant?.id;
  // Charts are tenant data: always read from the selected tenant's API, never
  // the central API. With no tenant selected there is nothing to show yet.
  const apiBase = selectedTenant?.api_base_url
    ? config.getTenantApiBase(selectedTenant.api_base_url)
    : null;
  const streamSections = streamState?.sections;
  const hasStreamSections = Boolean(streamSections && Object.keys(streamSections).length > 0);
  const noTenant = !hasStreamSections && (!selectedTenantId || !apiBase);

  useEffect(() => {
    // Clear anything rendered for a previous uuid/tenant before loading.
    setSections(null);
    setError(null);
    setLoading(true);

    if (streamSections && Object.keys(streamSections).length > 0) {
      // Build sections from Redux state — no fetch needed
      const built = SECTIONS_ORDER
        .filter((k) => streamSections[k]?.text)
        .map((k) => ({
          section_key: k,
          title: SECTION_LABELS[k] || k,
          content: streamSections[k].text,
        }));
      setSections(built);
      setLoading(false);
      return undefined;
    }

    // No tenant selected (e.g. a deep link before a tenant is chosen): stop
    // loading and render a prompt instead of spinning forever.
    if (!selectedTenantId || !apiBase) {
      setLoading(false);
      return undefined;
    }

    // Ignore a response that arrives after the tenant or uuid has changed.
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) throw new Error('Not authenticated');
        const token = session.tokens.idToken.toString();
        const res = await fetch(`${apiBase}/api/v1/medchart/cynthetic-admissions/${uuid}/sections`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        if (!cancelled) setSections(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uuid, streamSections, apiBase, selectedTenantId]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Generated Medical Record
        </Typography>
        <Chip label={`UUID: ${uuid?.slice(0, 8)}…`} size="small" variant="outlined" />
      </Box>

      {loading && (
        <Box display="flex" alignItems="center" gap={1.5} mt={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading sections…</Typography>
        </Box>
      )}

      {noTenant && (
        <Typography color="text.secondary">Select a tenant to view this chart.</Typography>
      )}

      {error && (
        <Typography color="error" variant="body2">{error}</Typography>
      )}

      {sections && sections.length === 0 && (
        <Typography color="text.secondary">No sections generated yet.</Typography>
      )}

      {sections && sections.map((s) => (
        <Paper key={s.section_key} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {s.title || SECTION_LABELS[s.section_key] || s.section_key}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <SectionRenderer sectionKey={s.section_key} text={s.content} />
        </Paper>
      ))}
    </Box>
  );
}
